/**
 * Doppelhelix Block — gemeinsame Interaktion fuer Rad, Licht, Partikel und Hintergrund.
 *
 * Das Ziehen verschiebt weiterhin die gemeinsame Web-Animations-Zeitachse der
 * 3D-Szene. Mauslicht und Partikelparallaxe werden davon getrennt geglaettet,
 * damit auch ein blosses Ueberfahren des Blocks Tiefe vermittelt. Der
 * Bildhintergrund beobachtet in groben Intervallen, welche Karte der visuellen
 * Mitte am naechsten ist, und blendet zwei vorbereitete Ebenen ueber Kreuz.
 */

const FRICTION = 0.955;
const MIN_VELOCITY = 0.01;
const MAX_TURNS_PER_SECOND = 1.5;
const TURNS_PER_WIDTH = 0.5;
const AUTO_RESUME_RETAIN = 0.86;
const BACKDROP_INTERVAL = 420;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function initPointerScene(block, viewport, reducedMotion) {
    if (block.dataset.helixPointer !== '1' || reducedMotion) {
        return;
    }

    let targetX = 0;
    let targetY = 0;
    let shownX = 0;
    let shownY = 0;
    let frame = null;
    let suspended = false;

    const apply = () => {
        block.style.setProperty('--zgg-pointer-near-x', `${shownX * 22}px`);
        block.style.setProperty('--zgg-pointer-near-y', `${shownY * 15}px`);
        block.style.setProperty('--zgg-pointer-middle-x', `${shownX * 9}px`);
        block.style.setProperty('--zgg-pointer-middle-y', `${shownY * 6}px`);
        block.style.setProperty('--zgg-pointer-far-x', `${shownX * -12}px`);
        block.style.setProperty('--zgg-pointer-far-y', `${shownY * -8}px`);
        block.style.setProperty('--zgg-bg-x', `${shownX * -14}px`);
        block.style.setProperty('--zgg-bg-y', `${shownY * -9}px`);
        block.style.setProperty('--zgg-light-x', `${50 + shownX * 28}%`);
        block.style.setProperty('--zgg-light-y', `${38 + shownY * 24}%`);
    };

    const tick = () => {
        if (suspended) {
            frame = null;
            return;
        }

        shownX += (targetX - shownX) * 0.16;
        shownY += (targetY - shownY) * 0.16;
        apply();

        if (Math.abs(targetX - shownX) > 0.002 || Math.abs(targetY - shownY) > 0.002) {
            frame = requestAnimationFrame(tick);
            return;
        }

        shownX = targetX;
        shownY = targetY;
        apply();
        frame = null;
    };

    const start = () => {
        if (frame === null) {
            frame = requestAnimationFrame(tick);
        }
    };

    viewport.addEventListener('pointermove', (event) => {
        if (suspended) {
            return;
        }

        const bounds = viewport.getBoundingClientRect();

        targetX = clamp(((event.clientX - bounds.left) / Math.max(1, bounds.width)) * 2 - 1, -1, 1);
        targetY = clamp(((event.clientY - bounds.top) / Math.max(1, bounds.height)) * 2 - 1, -1, 1);
        start();
    }, { passive: true });

    viewport.addEventListener('pointerleave', () => {
        targetX = 0;
        targetY = 0;
        start();
    }, { passive: true });

    viewport.addEventListener('zgghelixdragstart', () => {
        suspended = true;

        if (frame !== null) {
            cancelAnimationFrame(frame);
            frame = null;
        }
    });

    viewport.addEventListener('zgghelixdragend', () => {
        suspended = false;
        targetX = 0;
        targetY = 0;
        start();
    });
}

function initImageBackdrop(block, viewport, reducedMotion) {
    const backdrop = block.querySelector('[data-helix-background="image"]');

    if (!backdrop) {
        return;
    }

    const layers = Array.from(backdrop.querySelectorAll('.zgg-doppelhelix__backdrop-image'));
    const cards = Array.from(block.querySelectorAll('[data-helix-image]'));

    if (layers.length < 2 || !cards.length || reducedMotion) {
        return;
    }

    let activeIndex = Math.max(0, layers.findIndex((layer) => layer.classList.contains('is-active')));
    let activeUrl = cards[0].dataset.helixImage || '';
    let pendingUrl = '';
    let preloadToken = 0;
    let interval = null;

    const closestImage = () => {
        const viewportBounds = viewport.getBoundingClientRect();
        const centerX = viewportBounds.left + viewportBounds.width / 2;
        const centerY = viewportBounds.top + viewportBounds.height / 2;
        let best = null;

        cards.forEach((item) => {
            const card = item.querySelector('.zgg-doppelhelix__card');
            const url = item.dataset.helixImage;

            if (!card || !url) {
                return;
            }

            const bounds = card.getBoundingClientRect();

            if (bounds.bottom < viewportBounds.top || bounds.top > viewportBounds.bottom || bounds.width < 2) {
                return;
            }

            const distanceY = Math.abs(bounds.top + bounds.height / 2 - centerY) / Math.max(1, viewportBounds.height);
            const distanceX = Math.abs(bounds.left + bounds.width / 2 - centerX) / Math.max(1, viewportBounds.width);
            // Vordere Karten sind perspektivisch groesser und sollen bei fast
            // gleicher Mittendistanz den Hintergrund bestimmen.
            const apparentDepth = bounds.width / Math.max(1, viewportBounds.width);
            const score = distanceY + distanceX * 0.28 - apparentDepth * 0.72;

            if (!best || score < best.score) {
                best = { score, url };
            }
        });

        return best?.url || '';
    };

    const crossfade = (url) => {
        if (!url || url === activeUrl || url === pendingUrl) {
            return;
        }

        const token = ++preloadToken;
        const preload = new Image();
        pendingUrl = url;

        preload.onload = () => {
            if (token !== preloadToken) {
                return;
            }

            const nextIndex = activeIndex === 0 ? 1 : 0;
            const nextLayer = layers[nextIndex];

            nextLayer.style.backgroundImage = `url("${url.replace(/["\\\n\r]/g, '\\$&')}")`;
            nextLayer.classList.add('is-active');
            layers[activeIndex].classList.remove('is-active');
            activeIndex = nextIndex;
            activeUrl = url;
            pendingUrl = '';
        };

        preload.onerror = () => {
            if (token === preloadToken) {
                pendingUrl = '';
            }
        };

        preload.src = url;
    };

    const update = () => {
        // getBoundingClientRect() synchronisiert Layout. Waere das waehrend
        // eines Drags aktiv, koennte es die fluessige Zeitachsenverschiebung
        // in regelmaessigen Abstaenden ausbremsen.
        if (!block.classList.contains('is-dragging') && !block.classList.contains('is-gliding')) {
            crossfade(closestImage());
        }
    };
    const start = () => {
        if (interval === null) {
            update();
            interval = window.setInterval(update, BACKDROP_INTERVAL);
        }
    };
    const stop = () => {
        if (interval !== null) {
            window.clearInterval(interval);
            interval = null;
        }
    };

    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                start();
            } else {
                stop();
            }
        }, { rootMargin: '15% 0px' });

        observer.observe(viewport);
    } else {
        start();
    }
}

function initDrag(block, viewport, stage) {
    if (block.dataset.helixDrag !== '1' || !stage.getAnimations) {
        return;
    }

    const spin = parseFloat(block.dataset.helixSpin) || 12;
    const sign = parseFloat(block.dataset.helixDragSign) || 1;
    const wrap = parseFloat(block.dataset.helixWrap) || 0;
    const maxVelocity = spin * MAX_TURNS_PER_SECOND;
    const wrapMilliseconds = wrap * 1000;
    const automaticRates = new WeakMap();

    let tracked = [];
    let linked = [];
    let target = 0;
    let shown = 0;
    let velocity = 0;
    let glideDelta = 0;
    let glideRate = 0;
    let automaticRate = 1;
    let dragging = false;
    let gliding = false;
    let pointerId = null;
    let lastX = 0;
    let lastPointerTime = 0;
    let lastMovementTime = 0;
    let lastFrame = 0;
    let frame = null;

    const normalizeTime = (currentTime) => {
        if (wrapMilliseconds <= 0) {
            return currentTime;
        }

        return ((currentTime % wrapMilliseconds) + wrapMilliseconds) % wrapMilliseconds
            + wrapMilliseconds;
    };

    const collect = () => {
        const entries = stage.getAnimations({ subtree: true })
            .map((animation) => {
                if (typeof CSSTransition !== 'undefined' && animation instanceof CSSTransition) {
                    return null;
                }

                const element = animation.effect?.target;

                if (!(element instanceof Element) || !element.matches([
                    '.zgg-doppelhelix__node',
                    '.zgg-doppelhelix__spin',
                    '.zgg-doppelhelix__orbit',
                    '.zgg-doppelhelix__face',
                ].join(', '))) {
                    return null;
                }

                const currentTime = animation.currentTime;

                if (currentTime === null) {
                    return null;
                }

                if (!automaticRates.has(animation)) {
                    automaticRates.set(animation, animation.playbackRate || 1);
                }

                const wasPaused = animation.playState === 'paused';
                const baseRate = automaticRates.get(animation);

                return {
                    animation,
                    element,
                    wasPaused,
                    baseRate,
                    currentTime,
                };
            })
            .filter(Boolean);

        if (!entries.length) {
            tracked = [];
            linked = [];
            return;
        }

        // Alle geometrischen und optischen Teilanimationen gehoeren zu einer
        // gemeinsamen CSS-Zeitachse. Ein einziger Referenzwert korrigiert hier
        // auch kleinste, eventuell von frueheren Gesten geerbte Abweichungen.
        const baseTime = normalizeTime(entries[0].currentTime);

        entries.forEach((entry) => {
            // Greifen bedeutet Stillstand: Die automatische CSS-Zeitachse
            // darf waehrend des Haltens nicht gegen den Zeiger weiterlaufen.
            entry.animation.pause();
            entry.animation.playbackRate = entry.baseRate;
            entry.animation.currentTime = baseTime;
            entry.baseTime = baseTime;
        });

        tracked = entries.filter((entry) => !entry.element.matches('.zgg-doppelhelix__face'));
        linked = entries.filter((entry) => entry.element.matches('.zgg-doppelhelix__face'));
    };

    const applyEntries = (entries) => {
        const shift = shown * 1000;

        entries.forEach((entry) => {
            entry.animation.currentTime = entry.baseTime + shift;
        });
    };

    const allEntries = () => [...tracked, ...linked];

    const setGlideDelta = (delta, immediate = false) => {
        allEntries().forEach((entry) => {
            if (entry.wasPaused) {
                return;
            }

            const rate = entry.baseRate + delta;

            if (!immediate && entry.animation.updatePlaybackRate) {
                entry.animation.updatePlaybackRate(rate);
            } else {
                entry.animation.playbackRate = rate;
            }
        });
    };

    const finishGlide = () => {
        setGlideDelta(0);
        gliding = false;
        glideDelta = 0;
        glideRate = automaticRate;
        block.classList.remove('is-gliding');
        viewport.dispatchEvent(new Event('zgghelixdragend'));
    };

    const wrapTarget = () => {
        if (wrap <= 0) {
            return;
        }

        while (target > wrap) {
            target -= wrap;
            shown -= wrap;
        }

        while (target < -wrap) {
            target += wrap;
            shown += wrap;
        }
    };

    const drawDrag = () => {
        shown = target;
        applyEntries(tracked);
        // Position, Bahnform und Tiefenschaerfe muessen im selben Frame landen.
        // Schon ein ausgelassener Fokus-Frame ist bei einem schnellen Wisch als
        // seitlich versetzte scharfe Zone sichtbar.
        applyEntries(linked);

        frame = null;
    };

    const tickGlide = (now) => {
        const elapsed = Math.min(0.1, Math.max(0.001, (now - lastFrame) / 1000));
        lastFrame = now;

        const isReturning = glideRate * automaticRate <= 0
            || Math.abs(glideRate) <= Math.abs(automaticRate);
        const retain = isReturning ? AUTO_RESUME_RETAIN : FRICTION;

        // Die Eigenrotation wirkt durchgehend als Zielkraft. Auch ein Wurf in
        // Gegenrichtung wird deshalb in einer einzigen Kurve abgebremst,
        // durchlaeuft den Nullpunkt und geht ohne Stillstandsphase in das
        // normale Tempo ueber. In Laufrichtung bleibt der laengere Auslauf.
        glideDelta *= Math.pow(retain, elapsed * 60);
        glideRate = automaticRate + glideDelta;

        if (Math.abs(glideDelta) <= MIN_VELOCITY) {
            finishGlide();
            frame = null;
            return;
        }

        setGlideDelta(glideDelta);
        frame = requestAnimationFrame(tickGlide);
    };

    const startDraw = () => {
        if (frame === null) {
            frame = requestAnimationFrame(drawDrag);
        }
    };

    const secondsPerPixel = () => (
        (spin * TURNS_PER_WIDTH * sign) / Math.max(1, viewport.clientWidth)
    );

    const onPointerDown = (event) => {
        if (pointerId !== null || (event.pointerType === 'mouse' && event.button !== 0)) {
            return;
        }

        if (frame !== null) {
            cancelAnimationFrame(frame);
            frame = null;
        }

        gliding = false;
        glideDelta = 0;
        block.classList.remove('is-gliding');
        collect();
        target = 0;
        shown = 0;

        pointerId = event.pointerId;
        lastX = event.clientX;
        lastPointerTime = event.timeStamp;
        lastMovementTime = event.timeStamp;
        velocity = 0;
        dragging = true;

        block.classList.add('is-dragging');
        viewport.dispatchEvent(new Event('zgghelixdragstart'));
        viewport.setPointerCapture(pointerId);
    };

    const onPointerMove = (event) => {
        if (event.pointerId !== pointerId) {
            return;
        }

        const coalesced = event.getCoalescedEvents ? event.getCoalescedEvents() : [];
        const samples = coalesced.length ? coalesced : [event];
        const scale = secondsPerPixel();

        samples.forEach((sample) => {
            const pixelDelta = sample.clientX - lastX;

            if (pixelDelta === 0) {
                return;
            }

            const elapsed = Math.min(0.08, Math.max(0.001, (sample.timeStamp - lastPointerTime) / 1000));
            const timeDelta = pixelDelta * scale;
            const instant = timeDelta / elapsed;

            target += timeDelta;
            velocity = clamp(velocity * 0.52 + instant * 0.48, -maxVelocity, maxVelocity);
            lastX = sample.clientX;
            lastPointerTime = sample.timeStamp;
            lastMovementTime = sample.timeStamp;
        });

        wrapTarget();
        startDraw();
    };

    const onPointerUp = (event) => {
        if (event.pointerId !== pointerId) {
            return;
        }

        if (viewport.hasPointerCapture(pointerId)) {
            viewport.releasePointerCapture(pointerId);
        }

        if (event.clientX !== lastX) {
            onPointerMove(event);
        }

        if (event.type === 'pointercancel') {
            velocity = 0;
        } else {
            // Wer nach einer Bewegung nur noch festhaelt, wirft beim Loslassen
            // keinen alten Impuls ab. Das verhindert unerwartete Gegenbewegung.
            const idleSeconds = Math.max(0, (event.timeStamp - lastMovementTime) / 1000);
            velocity *= Math.exp(-idleSeconds * 18);

            if (idleSeconds > 0.18) {
                velocity = 0;
            }
        }

        if (frame !== null) {
            cancelAnimationFrame(frame);
            frame = null;
        }

        shown = target;
        applyEntries(tracked);
        applyEntries(linked);

        pointerId = null;
        dragging = false;
        block.classList.remove('is-dragging');
        const runningEntries = allEntries().filter((entry) => !entry.wasPaused);

        if (!runningEntries.length) {
            viewport.dispatchEvent(new Event('zgghelixdragend'));
            return;
        }

        automaticRate = runningEntries[0].baseRate;

        // Beim Loslassen ist die gemessene Zeigergeschwindigkeit die echte
        // Anfangsgeschwindigkeit. Die automatische Rotation wirkt erst danach
        // wieder als Zielkraft — beim blossen Halten gibt es keinen Sprung.
        glideRate = velocity;
        glideDelta = glideRate - automaticRate;

        gliding = Math.abs(glideDelta) > MIN_VELOCITY;
        setGlideDelta(glideDelta, true);
        runningEntries.forEach((entry) => entry.animation.play());

        if (!gliding) {
            finishGlide();
            return;
        }

        block.classList.add('is-gliding');
        lastFrame = performance.now();
        frame = requestAnimationFrame(tickGlide);
    };

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    viewport.addEventListener('dragstart', (event) => event.preventDefault());
}

function initHelix(block) {
    const viewport = block.querySelector('.zgg-doppelhelix__viewport');
    const stage = block.querySelector('.zgg-doppelhelix__stage');

    if (!viewport || !stage || block.dataset.helixBound) {
        return;
    }

    block.dataset.helixBound = '1';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    initPointerScene(block, viewport, reducedMotion);
    initImageBackdrop(block, viewport, reducedMotion);
    initDrag(block, viewport, stage);
}

function initAll() {
    document.querySelectorAll('[data-zgg-doppelhelix]').forEach(initHelix);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}
